import React, { useEffect, useState, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { config } from "../config";
import { mockBusStopData, mockPatternData } from "../mockData";

interface Arrival {
  id: string;
  headsign: string;
  destination: string;
  subRoute: string;
  direction: string;
  arrive: number;
  depart: number;
  arriveScheduled: number;
  departScheduled: number;
  realtime: boolean;
}

interface StopData {
  id: string;
  publicCode: string;
  name: string;
  coordinates: [number, number];
  routes: string[];
  arrivals: Arrival[];
}

interface ArrivalWithStop extends Arrival {
  stopName: string;
}

interface PatternData {
  id: string;
  headsign: string;
  direction: string;
  coordinates: [number, number][];
}

const StopView: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [arrivals, setArrivals] = useState<ArrivalWithStop[]>([]);
  const [patterns, setPatterns] = useState<Map<string, PatternData>>(new Map());
  const [patternRoutes, setPatternRoutes] = useState<Map<string, string>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const hasFetchedData = useRef(false);
  const hasFitBounds = useRef(false);
  const stopIds = ["06461", "06460", "00158", "08873", "00014"];
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clock timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatClockTime = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return "";
    return `Updated ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };


  useEffect(() => {
    // Skip initial fetch if data already loaded
    if (hasFetchedData.current && arrivals.length > 0) {
      console.log("Skipping data fetch - already loaded");
      return;
    }

    const fetchStopData = async () => {
      try {
        let allStopData: StopData[];

        if (config.useMocks) {
          // Use mock data
          allStopData = stopIds.map(stopId => mockBusStopData(stopId));
        } else {
          // Fetch real data
          // Add 2 weeks (14 days) to current time to access the new feed that starts Dec 7
          const timestamp = config.twoWeekOffset
            ? Date.now() + (14 * 24 * 60 * 60 * 1000)
            : Date.now();
          const promises = stopIds.map((stopId) =>
            fetch(
              `${config.services.RTA.stopDetails}${stopId}?timestamp=${timestamp}`,
              {
                headers: {
                  "X-Organization-Id": "c3b8f441-7a4b-46a9-b582-2c7ac3cb6f3f",
                },
              }
            ).then((res) => res.json())
          );

          allStopData = await Promise.all(promises);
        }

        console.log("Fetched stop data:", allStopData);

        const allArrivals: ArrivalWithStop[] = allStopData.flatMap((stopData) =>
          stopData.arrivals.map((arrival) => ({
            ...arrival,
            stopName: stopData.name,
          }))
        );

        const sortedArrivals = allArrivals
          .sort((a, b) => a.arrive - b.arrive)
          .slice(0, 9);

        setArrivals(sortedArrivals);

        // Build mapping from pattern ID to subRoute
        const routeMapping = new Map<string, string>();
        sortedArrivals.forEach((arrival) => {
          routeMapping.set(arrival.id, arrival.subRoute);
        });
        setPatternRoutes(routeMapping);

        // Only fetch patterns on first load
        if (!hasFetchedData.current) {
          const uniquePatternIds = Array.from(
            new Set(sortedArrivals.map((a) => a.id))
          );

          let patternDataArray: PatternData[];

          if (config.useMocks) {
            // Use mock data
            patternDataArray = uniquePatternIds.map(patternId => mockPatternData(patternId));
          } else {
            // Fetch real data
            // Add 2 weeks (14 days) to current time to access the new feed that starts Dec 7
            const futureTimestamp = config.twoWeekOffset
              ? Date.now() + (14 * 24 * 60 * 60 * 1000)
              : Date.now();
            const patternPromises = uniquePatternIds.map((patternId) =>
              fetch(`${config.services.RTA.patternDetails}${patternId}?timestamp=${futureTimestamp}`, {
                headers: {
                  "X-Organization-Id": "c3b8f441-7a4b-46a9-b582-2c7ac3cb6f3f",
                },
              }).then((res) => res.json())
            );

            patternDataArray = await Promise.all(patternPromises);
          }
          console.log("Fetched patterns:", patternDataArray);
          const patternMap = new Map<string, PatternData>();
          patternDataArray.forEach((pattern: PatternData) => {
            patternMap.set(pattern.id, pattern);
          });
          console.log("Pattern map size:", patternMap.size);
          setPatterns(patternMap);
        }

        hasFetchedData.current = true;
        setLastUpdated(new Date());
        setLoading(false);
      } catch (error) {
        console.error("Error fetching stop data:", error);
        setLoading(false);
      }
    };

    fetchStopData();
    const interval = setInterval(fetchStopData, config.dataRefreshInterval);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const initializeMap = () => {
      if (!mapContainer.current) {
        console.log("Map container not ready, retrying...");
        return false;
      }

      if (map.current) {
        console.log("Map already initialized");
        // Ensure map is properly sized
        setTimeout(() => {
          map.current?.resize();
        }, 100);
        return true;
      }

      const width = mapContainer.current.offsetWidth;
      const height = mapContainer.current.offsetHeight;

      // Don't initialize if container has no size
      if (width === 0 || height === 0) {
        console.log("Container has no dimensions, retrying...");
        return false;
      }

      console.log("Initializing map with dimensions:", width, height);

      mapboxgl.accessToken = config.map.apiKey;

      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: "mapbox://styles/mapbox/light-v11",
          center: config.map.center,
          zoom: config.map.zoom,
        });

        map.current.on("load", () => {
          console.log("Map fully loaded");
          setMapReady(true);
          setMapLoading(false);
        });

        map.current.on("error", (e) => {
          console.error("Map error:", e.error?.message || e.error);
          setMapLoading(false);
        });

        return true;
      } catch (error) {
        console.error("Error creating map:", error);
        return false;
      }
    };

    const attemptInit = () => {
      if (!initializeMap()) {
        retryTimeout = setTimeout(attemptInit, 100);
      }
    };

    let retryTimeout: NodeJS.Timeout;
    attemptInit();

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      // Don't remove map on cleanup to preserve state during hot reload
      // if (map.current) {
      //   map.current.remove();
      //   map.current = null;
      // }
    };
  }, []);

  useEffect(() => {
    if (!map.current || !mapReady || patterns.size === 0) {
      console.log(
        "Not adding patterns. MapReady:",
        mapReady,
        "Patterns size:",
        patterns.size
      );
      return;
    }

    // Additional check: ensure style is actually loaded
    if (!map.current.isStyleLoaded()) {
      console.log("Style not loaded yet, waiting...");
      return;
    }

    console.log("Adding patterns to map. Count:", patterns.size);

    const allCoordinates: [number, number][] = [];

    try {
      // First pass: Add all line sources and layers
      patterns.forEach((pattern, patternId) => {
        const sourceId = `pattern-${patternId}`;
        const layerId = `pattern-${patternId}-layer`;

        if (map.current?.getSource(sourceId)) {
          console.log("Source already exists:", sourceId);
          return;
        }

        console.log(
          "Adding pattern:",
          patternId,
          "with",
          pattern.coordinates.length,
          "coordinates"
        );

        allCoordinates.push(pattern.coordinates[0]);

        map.current?.addSource(sourceId, {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {
              name: pattern.headsign,
            },
            geometry: {
              type: "LineString",
              coordinates: pattern.coordinates,
            },
          },
        });

        map.current?.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#860038",
            "line-width": 4,
            "line-opacity": 0.8,
          },
        });
      });
    } catch (error) {
      console.error("Error adding line layers:", error);
      return;
    }

    try {
      patterns.forEach((pattern, patternId) => {
        const circleId = `pattern-${patternId}-circle`;
        const labelId = `pattern-${patternId}-label`;

        if (map.current?.getLayer(circleId)) {
          return;
        }

        if (pattern.coordinates.length > 0) {
          const endCoord = pattern.coordinates[0];
          const routeLabel =
            patternRoutes.get(patternId) || patternId.split(":")[0];

          map.current?.addLayer({
            id: circleId,
            type: "circle",
            source: {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {},
                geometry: {
                  type: "Point",
                  coordinates: endCoord,
                },
              },
            },
            paint: {
              "circle-radius": 18,
              "circle-color": "#000000",
              "circle-opacity": 1,
            },
          });

          map.current?.addLayer({
            id: labelId,
            type: "symbol",
            source: {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {
                  label: routeLabel,
                },
                geometry: {
                  type: "Point",
                  coordinates: endCoord,
                },
              },
            },
            layout: {
              "text-field": ["get", "label"],
              "text-size": 16,
              "text-offset": [0, 0],
              "text-anchor": "center",
              "text-allow-overlap": true,
            },
            paint: {
              "text-color": "#ffffff",
            },
          });
        }
      });
    } catch (error) {
      console.error("Error adding circle/label layers:", error);
      return;
    }

    if (allCoordinates.length > 0 && map.current && !hasFitBounds.current) {
      try {
        const bounds = allCoordinates.reduce(
          (bounds, coord) => {
            return [
              [
                Math.min(bounds[0][0], coord[0]),
                Math.min(bounds[0][1], coord[1]),
              ],
              [
                Math.max(bounds[1][0], coord[0]),
                Math.max(bounds[1][1], coord[1]),
              ],
            ];
          },
          [
            [allCoordinates[0][0], allCoordinates[0][1]],
            [allCoordinates[0][0], allCoordinates[0][1]],
          ]
        ) as [[number, number], [number, number]];

        map.current.fitBounds(bounds, {
          padding: 80,
          maxZoom: 14,
        });
        hasFitBounds.current = true;
        console.log("Fit bounds to routes");
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }, [patterns, mapReady, patternRoutes]);

  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDelayStatus = (
    arrival: Arrival
  ): { text: string; color: string } => {
    const delay = arrival.arrive - arrival.arriveScheduled;
    const delayMinutes = Math.floor(delay / 60000);

    if (delayMinutes > 0) {
      return { text: `+${delayMinutes} min`, color: "#860038" };
    } else if (delayMinutes < 0) {
      return { text: `${delayMinutes} min`, color: "#fdbb30" };
    }
    return { text: "On Time", color: "#000000" };
  };

  const getMinutesUntil = (timestamp: number): number => {
    const now = Date.now();
    // If using 2-week offset, subtract it from the arrival timestamp to get correct minutes
    const adjustedTimestamp = config.twoWeekOffset
      ? timestamp - (14 * 24 * 60 * 60 * 1000)
      : timestamp;
    return Math.floor((adjustedTimestamp - now) / 60000);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading arrivals...</div>
      </div>
    );
  }

  if (arrivals.length === 0 && !loading) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>No upcoming arrivals</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.header}>
        <img
          src="/cavs-logo-2.png"
          alt="Cleveland Cavaliers"
          style={styles.logo}
        />
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>Upcoming Bus Departures</h1>
          {!isMobile && (
            <div style={styles.dateTime}>
              <span style={styles.time}>{formatClockTime(currentTime)}</span>
              <span style={styles.date}>{formatDate(currentTime)}</span>
            </div>
          )}
        </div>
      </div>

      <div style={isMobile ? styles.contentMobile : styles.content}>
        <div style={isMobile ? styles.mapSectionMobile : styles.mapSection}>
          {mapLoading && (
            <div style={styles.mapLoadingOverlay}>
              <div style={styles.spinner}></div>
              <div style={styles.mapLoadingText}>Loading map...</div>
            </div>
          )}
          <div ref={mapContainer} style={styles.map} />
        </div>

        <div style={isMobile ? styles.tableSectionMobile : styles.tableSection}>
          <div style={isMobile ? styles.tableContainerMobile : styles.tableContainer}>
            <table style={isMobile ? styles.tableMobile : styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={isMobile ? styles.thMobile : styles.th}>ROUTE</th>
                  <th style={isMobile ? styles.thMobile : styles.th}>STOP</th>
                  <th style={isMobile ? styles.thMobile : styles.th}>DESTINATION</th>
                  <th style={isMobile ? styles.thMobile : styles.th}>TIME</th>
                  <th style={isMobile ? styles.thMobile : styles.th}>MIN</th>
                  <th style={isMobile ? styles.thMobile : styles.th}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {arrivals.map((arrival, index) => {
                  const delay = getDelayStatus(arrival);
                  const minutesUntil = getMinutesUntil(arrival.arrive);
                  const isEven = index % 2 === 0;

                  return (
                    <tr
                      key={`${arrival.id}-${index}`}
                      style={{
                        ...styles.row,
                        backgroundColor: isEven
                          ? "rgba(253, 187, 48, 0.1)"
                          : "rgba(255, 255, 255, 0.95)",
                      }}
                    >
                      <td style={isMobile ? styles.routeCellMobile : styles.routeCell}>
                        <div style={isMobile ? styles.routeBadgeMobile : styles.routeBadge}>{arrival.subRoute}</div>
                      </td>
                      <td style={isMobile ? styles.tdMobile : styles.td}>{arrival.stopName}</td>
                      <td style={isMobile ? styles.tdMobile : styles.td}>{arrival.destination}</td>
                      <td style={isMobile ? styles.timeCellMobile : styles.timeCell}>
                        {formatTime(arrival.arrive)}
                      </td>
                      <td style={isMobile ? styles.minCellMobile : styles.minCell}>
                        <span
                          style={
                            minutesUntil <= 5
                              ? (isMobile ? styles.urgentMinMobile : styles.urgentMin)
                              : (isMobile ? styles.normalMinMobile : styles.normalMin)
                          }
                        >
                          {minutesUntil <= 0 ? "NOW" : `${minutesUntil}`}
                        </span>
                      </td>
                      <td style={{ ...(isMobile ? styles.statusCellMobile : styles.statusCell), color: delay.color }}>
                        {delay.text}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isMobile && (
            <div style={styles.footer}>
              <div style={styles.realtimeBadge}>
                <span style={styles.liveDot}></span>
                LIVE UPDATES
              </div>
              {lastUpdated && (
                <div style={styles.footerUpdated}>
                  {formatLastUpdated(lastUpdated)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: "100%",
    height: "100vh",
    backgroundColor: "#000000",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#860038",
    padding: "1vh 2vw",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    gap: "1vw",
    borderBottom: "2px solid #fdbb30",
    minHeight: "8vh",
    flexShrink: 0
  },
  logo: {
    height: "7vh",
    maxHeight: "80px",
    width: "auto",
    filter: "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))",
  },
  headerContent: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minWidth: 0
  },
  headerTitle: {
    color: "#fdbb30",
    fontSize: 'clamp(18px, 2vw, 28px)',
    fontWeight: "900",
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "2px",
    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
  },
  dateTime: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    flexShrink: 0
  },
  time: {
    color: '#fdbb30',
    fontSize: 'clamp(16px, 1.8vw, 26px)',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  date: {
    color: 'rgba(253, 187, 48, 0.8)',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  },
  content: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0
  },
  contentMobile: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    minHeight: 0
  },
  mapSection: {
    flex: "0 0 37%",
    backgroundColor: "#1a1a1a",
    minWidth: 0,
    minHeight: 0,
    boxShadow: "4px 0 20px rgba(0, 0, 0, 0.3)",
    position: "relative",
  },
  mapSectionMobile: {
    height: "300px",
    backgroundColor: "#1a1a1a",
    minWidth: 0,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
    position: "relative",
    flexShrink: 0
  },
  map: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  mapLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    zIndex: 1000,
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "5px solid rgba(253, 187, 48, 0.2)",
    borderTop: "5px solid #fdbb30",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  mapLoadingText: {
    color: "#fdbb30",
    fontSize: "clamp(14px, 1.5vw, 18px)",
    fontWeight: "600",
  },
  tableSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "clamp(20px, 3vh, 40px)",
    overflow: "auto",
    background: "linear-gradient(135deg, #000000 0%, #860038 100%)",
    minHeight: 0
  },
  tableSectionMobile: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "15px",
    overflow: "auto",
    background: "linear-gradient(135deg, #000000 0%, #860038 100%)",
    minHeight: 0
  },
  loadingText: {
    color: "#fdbb30",
    fontSize: "clamp(20px, 2.5vw, 32px)",
    fontWeight: "700",
    textAlign: "center",
    margin: "auto",
  },
  errorText: {
    color: "#860038",
    fontSize: "clamp(18px, 2.2vw, 30px)",
    fontWeight: "700",
    textAlign: "center",
    margin: "auto",
  },
  tableContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderRadius: "clamp(8px, 1vh, 12px)",
    overflow: "hidden",
    border: "clamp(1px, 0.2vh, 2px) solid #fdbb30",
    boxShadow: "0 0 10px 3px rgba(0, 0, 0, 0.4)",
    flex: 1,
    minHeight: 0
  },
  tableContainerMobile: {
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderRadius: "8px",
    overflow: "auto",
    border: "1px solid #fdbb30",
    boxShadow: "0 0 10px 3px rgba(0, 0, 0, 0.4)",
    flex: 1,
    minHeight: 0
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    boxShadow: "4px 0 20px rgba(0, 0, 0, 0.3)",
  },
  tableMobile: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "11px",
  },
  headerRow: {
    backgroundColor: "#000000",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  },
  th: {
    padding: "clamp(12px, 1.5vh, 18px) clamp(10px, 1.2vw, 16px)",
    textAlign: "left",
    color: "#fdbb30",
    fontSize: "clamp(11px, 1.1vw, 15px)",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "1px",
    borderBottom: "2px solid #860038",
  },
  thMobile: {
    padding: "8px 6px",
    textAlign: "left",
    color: "#fdbb30",
    fontSize: "9px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #860038",
  },
  row: {
    borderBottom: "1px solid rgba(4, 30, 66, 0.1)",
    transition: "all 0.2s ease",
  },
  td: {
    padding: "clamp(10px, 1.2vh, 16px) clamp(10px, 1.2vw, 16px)",
    fontSize: "clamp(12px, 1.2vw, 16px)",
    color: "#000000",
    fontWeight: "500",
  },
  tdMobile: {
    padding: "6px 4px",
    fontSize: "10px",
    color: "#000000",
    fontWeight: "500",
  },
  routeCell: {
    padding: "clamp(10px, 1.2vh, 16px) clamp(10px, 1.2vw, 16px)",
  },
  routeCellMobile: {
    padding: "6px 4px",
  },
  routeBadge: {
    display: "inline-block",
    backgroundColor: "#000000",
    color: "#fdbb30",
    padding: "clamp(8px, 1vh, 10px) clamp(14px, 1.4vw, 18px)",
    borderRadius: "clamp(16px, 1.8vh, 22px)",
    fontSize: "clamp(12px, 1.2vw, 16px)",
    fontWeight: "900",
    minWidth: "clamp(45px, 4vw, 55px)",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(4, 30, 66, 0.3)",
    border: "2px solid #860038",
  },
  routeBadgeMobile: {
    display: "inline-block",
    backgroundColor: "#000000",
    color: "#fdbb30",
    padding: "4px 8px",
    borderRadius: "10px",
    fontSize: "10px",
    fontWeight: "900",
    minWidth: "30px",
    textAlign: "center",
    boxShadow: "0 2px 6px rgba(4, 30, 66, 0.3)",
    border: "1px solid #860038",
  },
  timeCell: {
    padding: "clamp(10px, 1.2vh, 16px) clamp(10px, 1.2vw, 16px)",
    fontSize: "clamp(12px, 1.2vw, 16px)",
    color: "#000000",
    fontWeight: "700",
    whiteSpace: "nowrap"
  },
  timeCellMobile: {
    padding: "6px 4px",
    fontSize: "9px",
    color: "#000000",
    fontWeight: "700",
    whiteSpace: "nowrap"
  },
  minCell: {
    padding: "clamp(10px, 1.2vh, 16px) clamp(10px, 1.2vw, 16px)",
    fontSize: "clamp(14px, 1.4vw, 18px)",
    fontWeight: "700",
  },
  minCellMobile: {
    padding: "6px 4px",
    fontSize: "11px",
    fontWeight: "700",
  },
  urgentMin: {
    color: "#860038",
    fontSize: "clamp(13px, 1.2vw, 16px)",
    fontWeight: "900",
    padding: "clamp(5px, 0.6vh, 6px) clamp(10px, 1vw, 14px)",
    backgroundColor: "rgba(253, 187, 48, 0.2)",
    borderRadius: "clamp(8px, 0.8vh, 10px)",
    display: "inline-block",
  },
  urgentMinMobile: {
    color: "#860038",
    fontSize: "11px",
    fontWeight: "900",
    padding: "3px 6px",
    backgroundColor: "rgba(253, 187, 48, 0.2)",
    borderRadius: "6px",
    display: "inline-block",
  },
  normalMin: {
    color: "#000000",
    fontSize: "clamp(16px, 1.5vw, 20px)",
    fontWeight: "700",
  },
  normalMinMobile: {
    color: "#000000",
    fontSize: "12px",
    fontWeight: "700",
  },
  statusCell: {
    padding: "clamp(10px, 1vh, 12px) clamp(10px, 1vw, 12px)",
    fontSize: "clamp(11px, 1vw, 13px)",
    fontWeight: "800",
    textTransform: "uppercase",
  },
  statusCellMobile: {
    padding: "6px 4px",
    fontSize: "9px",
    fontWeight: "800",
    textTransform: "uppercase",
  },
  footer: {
    textAlign: "center",
    paddingTop: "clamp(20px, 3vh, 40px)",
    flexShrink: 0
  },
  footerUpdated: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "clamp(11px, 1vw, 14px)",
    fontWeight: "500",
    marginTop: "clamp(6px, 0.8vh, 8px)",
  },
  realtimeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'clamp(8px, 1vw, 12px)',
    backgroundColor: '#860038',
    color: '#fdbb30',
    padding: 'clamp(8px, 1vh, 12px) clamp(16px, 1.8vw, 24px)',
    borderRadius: 'clamp(15px, 1.5vh, 20px)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontWeight: '900',
    letterSpacing: '1px',
    boxShadow: '0 4px 12px rgba(134, 0, 56, 0.4)',
    border: '1px solid #fdbb30'
  },
  liveDot: {
    width: "clamp(10px, 1vw, 14px)",
    height: "clamp(10px, 1vw, 14px)",
    backgroundColor: "#fdbb30",
    borderRadius: "50%",
    display: "inline-block",
    animation: "pulse 2s ease-in-out infinite",
    boxShadow: "0 0 8px #fdbb30",
  },
};

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  if (!document.head.querySelector('[data-spinner-animation]')) {
    style.setAttribute('data-spinner-animation', 'true');
    document.head.appendChild(style);
  }
}

export default StopView;
